import React, {useEffect, useMemo, useRef, useState} from 'react';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {API_ENDPOINTS} from "../../../apiConfig";
import API from "../../../utils/API";
import Forms from "../../../components/Forms";
import DeleteIconButton from "../../../components/Forms/DeleteIconButton";
import EditIconButton from "../../../components/Forms/EditIconButton";
import PartOrderModal from "../../../components/PartOrderModal";
import {useModal} from "../../../contexts/ModalContext";
import routes from "../../../routesConfig";
import AdminDataView from "../../../components/AdminDataView";
import {getRowStatusClass} from "../../../utils/adminStatus";

function computeTotals(order) {
    const byCurrency = new Map();

    for (const item of (order.partList || [])) {
        const tp = item.totalPrice || {};
        const curr = tp.currency || 'CZK';
        const net = Number(tp.net) || 0;
        const gross = Number(tp.gross) || 0;

        const agg = byCurrency.get(curr) || {net: 0, gross: 0};
        agg.net += net;
        agg.gross += gross;
        byCurrency.set(curr, agg);
    }

    if (order.discount?.type && order.discount?.value) {
        for (const [curr, agg] of byCurrency) {
            if (order.discount.type === 'percentage') {
                const pct = Number(order.discount.value) || 0;
                agg.gross = agg.gross * (1 - pct / 100);
            } else if (order.discount.type === 'fixed') {
                if (byCurrency.size === 1) {
                    agg.gross = Math.max(0, agg.gross - Number(order.discount.value || 0));
                }
            }
            byCurrency.set(curr, agg);
        }
    }

    return byCurrency;
}

const fmt = (amount, currency) =>
    new Intl.NumberFormat('cs-CZ', {style: 'currency', currency}).format(amount || 0);

const PartOrders = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [filterText, setFilterText] = useState('');
    const [error, setError] = useState();
    const {openModal, confirm, stack} = useModal();

    const filterInputRef = useRef(null);

    const fetchOrders = async () => {
        const {data} = await API.get(API_ENDPOINTS.partOrders.list);
        const normalized = (data || []).map(o => ({...o, id: o._id}));
        setOrders(normalized);
        setFiltered(normalized);
        setFilterText('');
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    // Filtrování
    useEffect(() => {
        const q = filterText.toLowerCase().trim();
        if (!q) return setFiltered(orders);
        setFiltered(
            orders.filter(o => {
                const label = (o.label || '').toLowerCase();
                const orderId = (o.orderId || '').toLowerCase();
                const url = (o.orderDetailUrl || '').toLowerCase();
                return label.includes(q) || orderId.includes(q) || url.includes(q);
            })
        );
    }, [filterText, orders]);

    // Keyboard helper: fokus do filtru při psaní (bez modálů)
    useEffect(() => {
        if (stack.length > 0) return;
        const onKey = (e) => {
            const isPrintable = e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
            if (isPrintable && filterInputRef.current && document.activeElement !== filterInputRef.current) {
                filterInputRef.current.focus();
            }
            if (e.shiftKey && (e.key === 'N' || e.key === 'n')) {
                e.preventDefault();
                openOrderModal();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [stack.length]);

    const handleFilterKeyDown = (e) => {
        if (e.key === 'Escape') setFilterText('');
    };

    // Odstranění
    const askDelete = async (order) => {
        const result = await confirm(t('confirmOrderDeletion', {order: order.label}));
        if (result === true) {
            try {
                await API.delete(API_ENDPOINTS.partOrders.delete(order._id));
                setOrders(prev => prev.filter(o => o._id !== order._id));
            } catch (e) {
                setError(e);
                console.error(e);
            }
        }
    };

    // Sloupce
    const columns = useMemo(() => ([
        {
            key: 'label',
            name: t('label'),
            minWidth: 100,
            renderCell: ({row}) => <span title={row.label}>{row.label}</span>
        },
        {
            key: 'items',
            name: t('items'),
            renderCell: ({row}) => row.partList
                .map((item) => `${item.part?.name || '-'}-${(item.part?.part?.compatibleWith || []).map(c => c.code || c.name).join(', ')}`)
                .join(', ')
        },
        {
            key: 'orderId',
            name: t('orderId'),
            renderCell: ({row}) => row.partList.map((part) => part.orderId).join(', ')
        },
        {
            key: 'orderDate',
            name: t('orderDate'),
            renderCell: ({row}) => row.orderDate ? moment(row.orderDate).format('DD.MM.YYYY') : '—'
        },
        {
            key: 'finishedDate',
            name: t('finishedDate'),
            renderCell: ({row}) => row.finishedDate ? moment(row.finishedDate).format('DD.MM.YYYY') : '—'
        },
        {
            key: 'quantity',
            name: t('totalQuantity'),
            renderCell: ({row}) => (row.partList || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0)
        },
        {
            key: 'total',
            name: t('total'),
            renderCell: ({row}) => {
                const byCurr = computeTotals(row);
                if (byCurr.size === 0) return '—';
                // pokud je víc měn, ukaž součty odděleně
                const parts = [];
                for (const [curr, agg] of byCurr.entries()) {
                    parts.push(`${fmt(agg.gross, curr)}`);
                }
                return parts.join(' · ');
            }
        },
        {
            key: 'createdBy',
            name: t('createdBy'),
            renderCell: ({row}) =>
                row.createdBy ? `${row.createdBy.name} ${row.createdBy.surname}` : '—'
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            renderCell: ({row}) => moment(row.createdAt).format('DD.MM.YYYY HH:mm')
        },
        {
            key: 'actions',
            name: 'Akce',
            width: 110,
            renderCell: ({row}) => (
                <div className="flex items-center justify-end h-full w-full">
                    <EditIconButton title="Upravit objednávku" onClick={() => navigate(routes.admin.getPartOrderRepair(row._id))}/>
                    <DeleteIconButton title="Smazat objednávku" onClick={() => askDelete(row)}/>
                </div>
            )
        }
    ].map(c => ({...c, resizable: true}))), [t, navigate]);

    const resolveOrderStatus = (row) => {
        const total = row.partList?.length || 0;
        const delivered = row.partList?.filter(p => p.delivered)?.length || 0;
        if (total > 0 && delivered === total) return 'completed';
        if (!row.finishedDate && row.orderDate && moment().diff(moment(row.orderDate), 'days') > 30) {
            return 'cancelled';
        }
        return 'pending';
    };

    const rowClassRenderer = (row) => {
        if (!row.finishedDate && row.orderDate && moment().diff(moment(row.orderDate), 'days') > 30) {
            return 'row-deadline';
        }
        return getRowStatusClass(resolveOrderStatus(row));
    };

    const openOrderModal = (order) => {
        openModal(PartOrderModal, {order, onOrderSaved: fetchOrders}, {title: t('partOrders.addOrder')})
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <Forms.Button
                    label={t('partOrders.addOrder')}
                    onClick={() => openOrderModal()}
                />
                <div className="w-80">
                    <Forms.Text
                        ref={filterInputRef}
                        value={filterText}
                        onChange={(val) => setFilterText(val)}
                        onKeyDown={handleFilterKeyDown}
                        label={t('search')}
                        placeholder={t('searchByLabelOrId')}
                    />
                </div>
            </div>

            <AdminDataView
                section="part-orders"
                rows={filtered}
                columns={columns}
                resolveRowStatus={resolveOrderStatus}
                dataGridProps={{rowClassRenderer}}
            />
        </div>
    );
};

export default PartOrders;
